// Import Package References
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Modal from 'react-responsive-modal';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';

// Import Project References
import { fetchPharmacy, updatePharmacy, fetchPharmacies } from '../State/Actions/PharmacyActions';
import { DeviceSections } from './DeviceSections';
import { LoadingIndicator } from '../Shared/LoadingIndicator/LoadingIndicator';
import { Error } from '../Shared/Error/Error';
import { DeviceRowType } from './DeviceRowType';
import { DeviceRow } from './DeviceRow';
import { DeviceSection } from './DeviceSection';
import { EventsPage } from '../Pages/EventsPage';
import { DeviceStatusInformation } from '../Pages/DeviceStatusInformation';
import { DeviceAliases } from '../Pages/DeviceAliases';
import { AddDevice } from './AddDevice';


// COMPONENT

class PharmacyDetails extends Component {

    constructor(props) {
        super(props);

        this.state = {
            editing: false,
            modalShowing: false,
            modalText: '',
        }

        this.editedSections = {};

        this.handleUpdate = this.handleUpdate.bind(this)
        this.onCloseModal = this.onCloseModal.bind(this)
        this.handleAddDevice = this.handleAddDevice.bind(this)
    }

    componentDidMount() {
        // Fetch the pharmacy details to render.
        this.props.fetchPharmacy(this.props.pharmacyId);

        // Determine if there are any query string parameters. 
        let deviceAttached = queryString.parse(this.props.location.search, { ignoreQueryPrefix: true }).deviceAttached

        // If the device is attached then show the device attaching modal.
        if(deviceAttached) {
            this.setState({modalShowing : true, modalText: 'Attaching Device'});
            setTimeout(() => {
                this.setState({modalShowing : false, modalText: ''});
            }, 2000);
        }
    }

    componentDidUpdate(prevProps) {
        // Navigating between /pharmacy/:id routes reuses this instance, so
        // componentDidMount doesn't re-run. Reset edit state and re-fetch when
        // the pharmacy changes to avoid leaking edits or showing stale data.
        if (prevProps.pharmacyId !== this.props.pharmacyId) {
            this.editedSections = {};
            this.setState({ editing: false });
            this.props.fetchPharmacy(this.props.pharmacyId);
        }
    }

    toggleEditing() {
        // If we are toggling away from editing then perform validation, and save to Firebase.
        if (this.state.editing) {
            this.saveDevice();
            this.aliases.saveAliases();
        }

        this.setState({editing: !this.state.editing});
    }

    cancelEditing() {
        this.setState({editing: !this.state.editing });

        // Discard any in-progress edits.
        this.editedSections = {};

        // Fetch the servers copy.
        this.props.fetchPharmacy(this.props.pharmacyId);
    }

    handleUpdate(sections, sectionId, rowId) {
        sections[sectionId].rows[rowId].edited = true
        // Merge by section uid so edits from different DeviceSections instances
        // (pharmacy fields vs. devices) accumulate instead of overwriting each other.
        this.editedSections = Object.assign({}, this.editedSections, sections);
    }

    saveDevice() {
        let entityUpdate = {};

        Object.keys(this.editedSections).map(sectionKeys => {
            let section = this.editedSections[sectionKeys];
            
            Object.keys(section.rows).map(rowKeys => {
                let row = section.rows[rowKeys];
                
                if (row.rowData !== undefined && row.identifier !== undefined && row.identifier !== '' && row.edited) {
                    entityUpdate[row.identifier] = row.rowData
                }
            })
        })

        this.props.updatePharmacy(entityUpdate, this.props.pharmacy);

        // Clear the buffer so these edits can't be re-applied to another pharmacy.
        this.editedSections = {};
    }

    onCloseModal() {
        this.setState({modalShowing: false, modalText: '' })
    }


    removeDeviceFromPharmacy(deviceId) {
        let confirmed = confirm("Are you sure you want to detach this device?");

        if (confirmed) {
            let entityUpdate = {};
            let newPharmacy = Object.assign({}, this.props.pharmacy);
    
            newPharmacy.deviceUuids[deviceId].dateRemoved = moment().format("HH:mm ddd DD-MM-YYYY");

            if (newPharmacy.deviceUuids === undefined || newPharmacy.deviceUuids === null) {
                newPharmacy.deviceUuids = {};
            }

            if (newPharmacy.devices === undefined || newPharmacy.devices === null) {
                newPharmacy.devices = {};
            }
    
            entityUpdate['deviceUuids'] = newPharmacy.deviceUuids;
            // entityUpdate['devices'] = newPharmacy.devices;
            this.props.updatePharmacy(entityUpdate, this.props.pharmacy);
        }
    }
    

    generateDevicesSection(pharmacy, active = true) {
        // We have to manually define how the sections are laid out.
        
        // 1. Device Info and Events.
        let rows = {};
        let sections = {};
        let row;
        let section;

        // If there are any devices attached to this pharmacy then display these. 
        if (pharmacy.hasOwnProperty('devices')) {
            let devices = pharmacy.devices;
            let deviceUuids = pharmacy.deviceUuids;

            let count = 1;
            Object.keys(devices).filter((deviceKey) => {
                let deviceUuid = deviceUuids[deviceKey];
                
                if (deviceUuid.hasOwnProperty('dateRemoved')) {
                    if (active) {
                        return false
                    }

                    return true;
                }

                return active;
            }).map(deviceKey => {
                let device = devices[deviceKey];
                let deviceUuid = deviceUuids[deviceKey];

                rows = {};

                row = new DeviceRow("Device ID", DeviceRowType.BASIC, device.LatestSetupPharmacyId, false, null, '', '', false);
                rows[row.uid] = row;

                if (device.hasOwnProperty('eventSummary')) {
                    row = new DeviceRow("Device Events", DeviceRowType.BASIC, Object.keys(device.eventSummary).length + " events", true, <EventsPage device={device} />);
                    rows[row.uid] = row;
                }

                if (deviceUuid.hasOwnProperty('dateAdded')) {
                    row = new DeviceRow("Date Added", DeviceRowType.BASIC, moment(deviceUuid.dateAdded, "HH:MM ddd DD-MM-YYYY").format("HH:mm ddd DD-MM-YYYY"), false, null, '', '', false);
                    rows[row.uid] = row;
                }

                if (device.hasOwnProperty('DeviceDetails') && device.DeviceDetails.hasOwnProperty('DetailsTimestamp')) {
                    row = new DeviceRow("Date Active", DeviceRowType.BASIC, moment(device.DeviceDetails.DetailsTimestamp, "HH:MM ddd DD-MM-YYYY").format("HH:mm ddd DD-MM-YYYY"), false, null, '', '', false);
                    rows[row.uid] = row;
                }

                if (deviceUuid.hasOwnProperty('dateRemoved')) {
                    row = new DeviceRow("Date Ended", DeviceRowType.BASIC, moment(deviceUuid.dateRemoved).format("HH:mm ddd DD-MM-YYYY"), false, null, '', '', false);
                    rows[row.uid] = row;
                }

                // Get the device status information.
                if (device.hasOwnProperty('DeviceDetails')) {
                    if (device.DeviceDetails.hasOwnProperty('dynamicDevice')) {
                        row = new DeviceRow("Status", DeviceRowType.BASIC, device.LatestSetupPharmacyId, true, <DeviceStatusInformation device={device} />);
                        rows[row.uid] = row;
                    }
                }

                if(this.state.editing && active) {
                    row = new DeviceRow('', DeviceRowType.NEW, <button type="button" className="btn btn-danger btn-block" onClick={() => this.removeDeviceFromPharmacy(deviceKey)}>Remove from Pharmacy</button> , true, '');
                    rows[row.uid] = row;
                }
               
                section = new DeviceSection("Device " + count, rows, '');
                section.rows = rows;
                section.showSectionHeader = true;
                section.sectionHeader = 'Devices';

                sections[section.uid] = section;

                count += 1;
            });

        }

        return sections;
    }

    generatePharmacySection(pharmacy) {
        // We have to manually define how the sections are laid out.
        
        // 1. Device Info and Events.
        let rows = {};
        let sections = {};
        let row;
        let section;

        row = new DeviceRow("Campaign Name", DeviceRowType.BASIC, pharmacy.campaignName, false, null, 'campaignName', '', true);
        rows[row.uid] = row;

        row = new DeviceRow("MDM ID", DeviceRowType.BASIC, pharmacy.mdmId, false, null, 'mdmId', '');
        rows[row.uid] = row;

        row = new DeviceRow("Test Pharmacy", DeviceRowType.SWITCH, pharmacy.isTest != undefined ? pharmacy.isTest : false, false, null, 'isTest', '');
        rows[row.uid] = row;

        row = new DeviceRow("Deactivated", DeviceRowType.SWITCH, pharmacy.isDeactivated != undefined ? pharmacy.isDeactivated : false, false, null, 'isDeactivated', '');
        rows[row.uid] = row;

        section = new DeviceSection("Pharmacy Info", rows, ["Pharmacies", pharmacy.id]);
        section.showSectionHeader = true;
        section.sectionHeader = 'Pharmacy';

        section.rows = rows;
        sections[section.uid] = section;

        rows = {};
        row = new DeviceRow("Address", DeviceRowType.BASIC, pharmacy.address, false, null, 'address', '', true);
        rows[row.uid] = row;

        row = new DeviceRow("Phone", DeviceRowType.BASIC, pharmacy.phone, false, null, 'phone', '', true);
        rows[row.uid] = row;

        row = new DeviceRow("Tags", DeviceRowType.BASIC, pharmacy.tags, false, null, 'tags', '', true);
        rows[row.uid] = row;

        row = new DeviceRow("Location Code", DeviceRowType.BASIC, pharmacy.locationCode, false, null, 'locationCode', '', true);
        rows[row.uid] = row;

        row = new DeviceRow("Campaign Category", DeviceRowType.BASIC, pharmacy.campaignCategory, false, null, 'campaignCategory', '', true);
        rows[row.uid] = row;

        row = new DeviceRow("Associated Clinic Name", DeviceRowType.BASIC, pharmacy.associatedClinicName, false, null, 'associatedClinicName', '', true);
        rows[row.uid] = row;

        row = new DeviceRow("Associated Clinic Person", DeviceRowType.BASIC, pharmacy.associatedClinicPerson, false, null, 'associatedClinicPerson', '', true);
        rows[row.uid] = row;

        row = new DeviceRow("Promotion Manager", DeviceRowType.BASIC, pharmacy.promotionManager, false, null, 'promotionManager', '', true);
        rows[row.uid] = row;

        section = new DeviceSection("Optional Pharmacy Details", rows, ["Pharmacies", pharmacy.id]);
        section.rows = rows;
        sections[section.uid] = section;

        rows = {};

        let aliases = [];
        if (pharmacy.aliases !== undefined) {
            aliases = Object.keys(pharmacy.aliases);
        } 
        
        
        if (aliases.length > 0) {
            // Check if any of the aliases are current.
            let activeAliasesCount = 0;
            let aliasesText = "No active aliases";

            aliases.forEach(aliasKey => {
                let alias = pharmacy.aliases[aliasKey];
                let startDate = moment(alias.startDate, "DD/MM/YYYY");
                let endDate = moment(alias.endDate, "DD/MM/YYYY");

                if(moment().isAfter(startDate), moment().isBefore(endDate)) {
                    activeAliasesCount += 1;
                }
            })

            if (activeAliasesCount > 0) {
                aliasesText = activeAliasesCount + " active aliases"
            }
            
            row = new DeviceRow(aliasesText, DeviceRowType.BASIC, aliases.length + " aliases total", true,  <DeviceAliases pharmacy={pharmacy} onRef={ref => (this.aliases = ref)} editing={this.state.editing} aliases={pharmacy.aliases} />, null, '');
        } else {
            row = new DeviceRow("No active aliases", DeviceRowType.BASIC, "0 aliases total", true,  <DeviceAliases pharmacy={pharmacy} onRef={ref => (this.aliases = ref)} editing={this.state.editing} device={this.props.device} />, null, '');
        }            

        rows[row.uid] = row;

        section = new DeviceSection("Pharmacy Alias", rows, ["Pharmacies", pharmacy.id]);
        section.rows = rows;
        sections[section.uid] = section;
    
        return sections;
    }
    

    handleAddDevice() {
        this.props.fetchPharmacy(this.props.pharmacyId);
    }

    render() {
        let activeDevices = this.generateDevicesSection(this.props.pharmacy);
        let inactiveDevices = this.generateDevicesSection(this.props.pharmacy, false);

        return (
            <div>
                {/* Modals */}
                <Modal open={this.state.modalShowing} onClose={this.onCloseModal} center>
                    <div className="lds-dual-ring"></div>
                    <div>{ this.state.modalText }</div>
                </Modal>

                <AddDevice handleAdd={this.handleAddDevice} pharmacyId={this.props.pharmacy.id} />
                
                <nav className="subnav navbar-light bg-light">
                    <div className="container">
                        <div className="subnav-title">{this.props.pharmacy.campaignName}</div>
                    </div>
                </nav>


                <div className="container">
                    {/* Show the Pharmacy details when it has been fetched. */}
                    { (this.props.fetched && !this.props.updatingPharmacy) && 
                        <Fragment>
                            {/* Editing Buttons */}
                            <button onClick={() => this.toggleEditing()} type="button" className="edit-button btn btn-primary">{ this.state.editing ? 'Save' : 'Edit' }</button>
                            { this.state.editing && <button onClick={() => this.cancelEditing()} type="button" className="cancel-button btn btn-danger">Cancel</button> }

                            <div className="section-header">
                                Pharmacy
                            </div>
                            <div>
                                <DeviceSections handleUpdate={this.handleUpdate} editing={this.state.editing} sections={this.generatePharmacySection(this.props.pharmacy)} />
                            </div>
                            { this.props.pharmacy.hasOwnProperty("devices") && 
                                <Fragment>
                                    <div className="section-header">
                                        Devices
                                    </div>
                                    <div className="subsection-header">
                                        Active
                                    </div>
                                    { Object.keys(activeDevices).length > 0 ?
                                        <div>
                                            <DeviceSections handleUpdate={this.handleUpdate} editing={this.state.editing} sections={activeDevices} />
                                        </div>
                                    : 
                                        <div className="card">
                                            <div className="text-placeholder">No active devices.</div>
                                        </div>
                                    }
                                    <div className="subsection-header">
                                        Historical
                                    </div> 
                                    { Object.keys(inactiveDevices).length > 0 ?
                                        <div>
                                            <DeviceSections handleUpdate={this.handleUpdate} editing={this.state.editing} sections={inactiveDevices} />
                                        </div>
                                    : 
                                        <div className="card">
                                            <div className="text-placeholder">No historical devices.</div>
                                        </div>
                                    }
                                   
                                </Fragment>
                            }
                            <div>
                                <div className="add-device-section">
                                    <button type="button" className="btn btn-primary" data-toggle="modal" data-target="#addDeviceModal">
                                        Add Device
                                    </button>
                                </div>
                            </div>
                        </Fragment>
                    }

                    {/* Loading Indicator */}
                    { <LoadingIndicator busy={ this.props.fetching || this.props.updatingPharmacy } /> }
                    { this.props.failed && <Error message="Failed to retrieve pharmacy." /> }
                    </div>
                
            </div>
        );
    }
}


// CONFIGURE REACT REDUX
const mapStateToProps = state => {
    const { fetching, fetched, failed, pharmacy, pharmacies, updatingPharmacy, updatedPharmacy } = state.pharmaciesReducer;
    const { fetchingDevice } = state.devicesReducer;
    return { fetching, fetched, failed, pharmacy, pharmacies, updatingPharmacy, updatedPharmacy  };
};

const mapDispatchToProps = dispatch => (
    bindActionCreators({ fetchPharmacy, updatePharmacy, fetchPharmacies }, dispatch)
);

const hoc = withRouter(connect(mapStateToProps, mapDispatchToProps)(PharmacyDetails));


// EXPORT COMPONENT

export { hoc as PharmacyDetails };